/**
 * Structural metadata for Blink locations.
 *
 * Textual content (name, tagline, overview, pricing tiers, access notes, etc.)
 * lives in `locales/<lang>.json` under `locations.<slug>.*` so it can be
 * localized. This file holds non-textual or universal data: slug,
 * coordinates, image paths, the Google Maps embed URL, and contact info
 * (phone numbers, names, URLs are the same in every language).
 */

export interface LocationContact {
  /** Primary phone number for the location. */
  phone: string
  /** Customer service rep name (universal, not localized). */
  csName: string
  /** Customer service direct phone. */
  csPhone: string
  /** Official external homepage. */
  homepageUrl: string
}

export interface LocationMeta {
  slug: string
  /** Latitude/longitude for the embed and "open in Maps" links. */
  lat: number
  lng: number
  /** Iframe `src` URL for the embedded Google Map (no API key required). */
  mapEmbedSrc: string
  /** Image paths under `/public`. */
  images: string[]
  /** Optional contact info — omit for locations without a staffed counter. */
  contact?: LocationContact
}

export const locations: LocationMeta[] = [
  {
    slug: 'umimachi',
    // Umimachi Terrace, Oarai-machi, Ibaraki — next to Oarai Station.
    lat: 36.3147763,
    lng: 140.5628511,
    mapEmbedSrc:
      'https://www.google.com/maps?q=36.3147763,140.5628511&t=&z=17&ie=UTF8&iwloc=&output=embed',
    images: [
      '/images/locations/umimachi/1.jpg',
      '/images/locations/umimachi/2.jpg',
      '/images/locations/umimachi/3.jpg',
      '/images/locations/umimachi/4.jpg',
      '/images/locations/umimachi/5.jpg',
      '/images/locations/umimachi/6.jpg',
      '/images/locations/umimachi/7.jpg',
      '/images/locations/umimachi/8.jpg',
      '/images/locations/umimachi/9.jpg',
      '/images/locations/umimachi/10.jpg',
      '/images/locations/umimachi/11.jpg'
    ],
    contact: {
      phone: '029-266-0788',
      csName: 'Takahashi',
      csPhone: '090-8460-0209',
      homepageUrl: 'https://www.oarai-info.jp/page/dir000476.html'
    }
  },
  {
    slug: 'naoshima-honmura',
    // TVC Naoshima Rental Honmura Branch — steps from Honmura Port ferry terminal.
    lat: 34.462,
    lng: 133.998,
    mapEmbedSrc:
      'https://www.google.com/maps?q=34.462,133.998&t=&z=17&ie=UTF8&iwloc=&output=embed',
    images: [
      '/images/locations/naoshima-honmura/2.jpg',
      '/images/locations/naoshima-honmura/4.jpg',
      '/images/locations/naoshima-honmura/1.jpg',
      '/images/locations/naoshima-honmura/3.jpg'
    ],
    contact: {
      phone: '087-802-3355',
      csName: 'Takahashi',
      csPhone: '090-8460-0209',
      homepageUrl: 'https://tvc-service.com/'
    }
  },
  {
    slug: 'minshuku-aoiso',
    // Minshuku Aoiso — traditional inn beside Lake Kawaguchi, Fuji Five Lakes.
    lat: 35.5085,
    lng: 138.761,
    mapEmbedSrc:
      'https://www.google.com/maps?q=35.5085,138.761&t=&z=17&ie=UTF8&iwloc=&output=embed',
    images: [
      '/images/locations/minshuku-aoiso/1.jpg',
      '/images/locations/minshuku-aoiso/2.jpg',
      '/images/locations/minshuku-aoiso/3.jpg',
      '/images/locations/minshuku-aoiso/4.jpg',
      '/images/locations/minshuku-aoiso/5.jpg'
    ],
    contact: {
      phone: '090-8006-6604',
      csName: 'Takahashi',
      csPhone: '090-8460-0209',
      homepageUrl: 'https://aoiso.jp/'
    }
  },
  {
    slug: 'asahikawa-cycle-fuji',
    // Cycle Shop Fuji Shoukai — downtown Asahikawa, established 1933.
    lat: 43.7658,
    lng: 142.3612,
    mapEmbedSrc:
      'https://www.google.com/maps?q=43.7658,142.3612&t=&z=17&ie=UTF8&iwloc=&output=embed',
    images: [
      '/images/locations/asahikawa-cycle-fuji/1.jpg',
      '/images/locations/asahikawa-cycle-fuji/2.jpg',
      '/images/locations/asahikawa-cycle-fuji/3.jpg',
      '/images/locations/asahikawa-cycle-fuji/4.jpg',
      '/images/locations/asahikawa-cycle-fuji/5.jpg'
    ],
    contact: {
      phone: '0166-23-4589',
      csName: 'Takahashi',
      csPhone: '090-8460-0209',
      homepageUrl: 'https://www.fujishoukai.com/'
    }
  },
  {
    slug: 'rikunominato-seidan',
    // Riku no Minato Seidan — Minami-Awaji cycle station & highway bus hub.
    lat: 34.3022,
    lng: 134.7404,
    mapEmbedSrc:
      'https://www.google.com/maps?q=34.3022,134.7404&t=&z=17&ie=UTF8&iwloc=&output=embed',
    images: [
      '/images/locations/rikunominato-seidan/1.jpg',
      '/images/locations/rikunominato-seidan/2.jpg',
      '/images/locations/rikunominato-seidan/3.jpg',
      '/images/locations/rikunominato-seidan/4.jpg',
      '/images/locations/rikunominato-seidan/5.jpg'
    ],
    contact: {
      phone: '0799-36-4422',
      csName: 'Takahashi',
      csPhone: '090-8460-0209',
      homepageUrl:
        'https://www.city.minamiawaji.hyogo.jp/soshiki/koutsuu/rikunominatoseidan.html'
    }
  },
  {
    slug: 'tanba-sasayama-sasayamaguchi-station',
    lat: 35.0563,
    lng: 135.1775,
    mapEmbedSrc:
      'https://www.google.com/maps?q=35.0563,135.1775&t=&z=17&ie=UTF8&iwloc=&output=embed',
    images: [
      '/images/locations/tanba-sasayama-sasayamaguchi-station/1.jpg',
      '/images/locations/tanba-sasayama-sasayamaguchi-station/2.jpg',
      '/images/locations/tanba-sasayama-sasayamaguchi-station/3.jpg',
      '/images/locations/tanba-sasayama-sasayamaguchi-station/4.jpg',
      '/images/locations/tanba-sasayama-sasayamaguchi-station/5.jpg'
    ],
    contact: {
      phone: '079-552-3380',
      csName: 'Takahashi',
      csPhone: '090-8460-0209',
      homepageUrl: 'https://tourism.sasayama.jp/rental-cycle/'
    }
  },
  {
    slug: 'tanba-sasayama-castle-town-hotel',
    lat: 35.0758,
    lng: 135.2193,
    mapEmbedSrc:
      'https://www.google.com/maps?q=35.0758,135.2193&t=&z=17&ie=UTF8&iwloc=&output=embed',
    images: [
      '/images/locations/tanba-sasayama-castle-town-hotel/1.jpg',
      '/images/locations/tanba-sasayama-castle-town-hotel/2.jpg',
      '/images/locations/tanba-sasayama-castle-town-hotel/3.jpg',
      '/images/locations/tanba-sasayama-castle-town-hotel/4.jpg',
      '/images/locations/tanba-sasayama-castle-town-hotel/5.jpg'
    ],
    contact: {
      phone: '0120-210-289',
      csName: 'Takahashi',
      csPhone: '090-8460-0209',
      homepageUrl: 'https://www.sasayamastay.jp/'
    }
  },
  {
    slug: 'tanba-sasayama-holonpia-hotel',
    lat: 35.0611,
    lng: 135.1714,
    mapEmbedSrc:
      'https://www.google.com/maps?q=35.0611,135.1714&t=&z=17&ie=UTF8&iwloc=&output=embed',
    images: [
      '/images/locations/tanba-sasayama-holonpia-hotel/1.jpg',
      '/images/locations/tanba-sasayama-holonpia-hotel/2.jpg',
      '/images/locations/tanba-sasayama-holonpia-hotel/3.jpg',
      '/images/locations/tanba-sasayama-holonpia-hotel/4.jpg',
      '/images/locations/tanba-sasayama-holonpia-hotel/5.jpg'
    ],
    contact: {
      phone: '079-594-2611',
      csName: 'Takahashi',
      csPhone: '090-8460-0209',
      homepageUrl: 'https://holonpia.com/'
    }
  },
  {
    slug: 'tanba-sasayama-joto-branch',
    lat: 35.0683,
    lng: 135.2835,
    mapEmbedSrc:
      'https://www.google.com/maps?q=35.0683,135.2835&t=&z=17&ie=UTF8&iwloc=&output=embed',
    images: [
      '/images/locations/tanba-sasayama-joto-branch/1.jpg',
      '/images/locations/tanba-sasayama-joto-branch/2.jpg',
      '/images/locations/tanba-sasayama-joto-branch/3.jpg',
      '/images/locations/tanba-sasayama-joto-branch/4.jpg',
      '/images/locations/tanba-sasayama-joto-branch/5.jpg'
    ],
    contact: {
      phone: '079-594-2611',
      csName: 'Takahashi',
      csPhone: '090-8460-0209',
      homepageUrl:
        'https://www.city.tambasasayama.lg.jp/soshikikarasagasu/shakaifukushika/kenko_fukushi/barrierfree/21741.html'
    }
  },
  {
    slug: 'tanba-sasayama-taki-branch',
    lat: 35.1042,
    lng: 135.3358,
    mapEmbedSrc:
      'https://www.google.com/maps?q=35.1042,135.3358&t=&z=17&ie=UTF8&iwloc=&output=embed',
    images: [
      '/images/locations/tanba-sasayama-taki-branch/1.jpg',
      '/images/locations/tanba-sasayama-taki-branch/2.jpg',
      '/images/locations/tanba-sasayama-taki-branch/3.jpg',
      '/images/locations/tanba-sasayama-taki-branch/4.jpg',
      '/images/locations/tanba-sasayama-taki-branch/5.jpg'
    ],
    contact: {
      phone: '079-594-2611',
      csName: 'Takahashi',
      csPhone: '090-8460-0209',
      homepageUrl:
        'https://www.city.tambasasayama.lg.jp/soshikikarasagasu/somuka/gyoseijoho/4/5469.html'
    }
  },
  {
    slug: 'tanba-sasayama-heartpia',
    lat: 35.0906,
    lng: 135.3149,
    mapEmbedSrc:
      'https://www.google.com/maps?q=35.0906,135.3149&t=&z=17&ie=UTF8&iwloc=&output=embed',
    images: [
      '/images/locations/tanba-sasayama-heartpia/1.jpg',
      '/images/locations/tanba-sasayama-heartpia/2.jpg',
      '/images/locations/tanba-sasayama-heartpia/3.jpg',
      '/images/locations/tanba-sasayama-heartpia/4.jpg',
      '/images/locations/tanba-sasayama-heartpia/5.jpg'
    ],
    contact: {
      phone: '079-594-2611',
      csName: 'Takahashi',
      csPhone: '090-8460-0209',
      homepageUrl:
        'https://www.city.tambasasayama.lg.jp/soshikikarasagasu/somuka/gyoseijoho/4/5469.html'
    }
  },
  {
    slug: 'tanba-sasayama-tourist-center',
    lat: 35.0733,
    lng: 135.2179,
    mapEmbedSrc:
      'https://www.google.com/maps?q=35.0733,135.2179&t=&z=17&ie=UTF8&iwloc=&output=embed',
    images: [
      '/images/locations/tanba-sasayama-tourist-center/1.jpg',
      '/images/locations/tanba-sasayama-tourist-center/2.jpg',
      '/images/locations/tanba-sasayama-tourist-center/3.jpg',
      '/images/locations/tanba-sasayama-tourist-center/4.jpg',
      '/images/locations/tanba-sasayama-tourist-center/5.jpg'
    ],
    contact: {
      phone: '079-552-3380',
      csName: 'Takahashi',
      csPhone: '090-8460-0209',
      homepageUrl: 'https://r.goope.jp/sasayama-kanko/'
    }
  },
  {
    slug: 'sonobe-station',
    // JR Sonobe Station West Exit — Nantan City bike rental, 11 e-bikes.
    lat: 35.1030,
    lng: 135.4813,
    mapEmbedSrc:
      'https://www.google.com/maps?q=35.1030,135.4813&t=&z=17&ie=UTF8&iwloc=&output=embed',
    images: [
      '/images/locations/sonobe-station/1.jpg',
      '/images/locations/sonobe-station/2.jpg',
      '/images/locations/sonobe-station/3.jpg',
      '/images/locations/sonobe-station/4.jpg',
      '/images/locations/sonobe-station/5.jpg'
    ],
    contact: {
      phone: '0771-62-0050',
      csName: 'Takahashi',
      csPhone: '090-8460-0209',
      homepageUrl: 'https://nantankanko.jp/'
    }
  },
  {
    slug: 'kinokawa-mikasakan',
    // Kinokawa Mikasakan — 3 Dahon Horize mini velos at Kokawa.
    lat: 34.2704,
    lng: 135.4041,
    mapEmbedSrc:
      'https://www.google.com/maps?q=34.2704,135.4041&t=&z=17&ie=UTF8&iwloc=&output=embed',
    images: [
      '/images/locations/kinokawa-mikasakan/1.jpg',
      '/images/locations/kinokawa-mikasakan/2.jpg',
      '/images/locations/kinokawa-mikasakan/3.jpg',
      '/images/locations/kinokawa-mikasakan/4.jpg',
      '/images/locations/kinokawa-mikasakan/5.jpg'
    ],
    contact: {
      phone: '0736-61-0012',
      csName: 'Takahashi',
      csPhone: '090-8460-0209',
      homepageUrl:
        'https://www.pref.wakayama.lg.jp/prefg/130200/rentalcycle/rentacycle.html'
    }
  },
  {
    slug: 'kinokawa-kirari',
    // Kirari (紀楽里) — 2 Daytona DE01 e-bikes at Kishigawa.
    lat: 34.2094,
    lng: 135.3120,
    mapEmbedSrc:
      'https://www.google.com/maps?q=34.2094,135.3120&t=&z=17&ie=UTF8&iwloc=&output=embed',
    images: [
      '/images/locations/kinokawa-kirari/1.jpg',
      '/images/locations/kinokawa-kirari/2.jpg',
      '/images/locations/kinokawa-kirari/3.jpg',
      '/images/locations/kinokawa-kirari/4.jpg',
      '/images/locations/kinokawa-kirari/5.jpg'
    ],
    contact: {
      phone: '0736-61-0012',
      csName: 'Takahashi',
      csPhone: '090-8460-0209',
      homepageUrl:
        'https://www.pref.wakayama.lg.jp/prefg/130200/rentalcycle/rentacycle.html'
    }
  },
  {
    slug: 'wakasa-takahama-kousen-ryokan',
    lat: 35.4966,
    lng: 135.5135,
    mapEmbedSrc:
      'https://www.google.com/maps?q=35.4966,135.5135&t=&z=17&ie=UTF8&iwloc=&output=embed',
    images: [
      '/images/locations/wakasa-takahama-kousen-ryokan/1.jpg',
      '/images/locations/wakasa-takahama-kousen-ryokan/2.jpg',
      '/images/locations/wakasa-takahama-kousen-ryokan/3.jpg',
      '/images/locations/wakasa-takahama-kousen-ryokan/4.jpg',
      '/images/locations/wakasa-takahama-kousen-ryokan/5.jpg'
    ],
    contact: {
      phone: '0770-72-0338',
      csName: 'Takahashi',
      csPhone: '090-8460-0209',
      homepageUrl: 'https://visittakahama.com/'
    }
  },
  {
    slug: 'wakasa-takahama-familiar',
    lat: 35.4915,
    lng: 135.5725,
    mapEmbedSrc:
      'https://www.google.com/maps?q=35.4915,135.5725&t=&z=17&ie=UTF8&iwloc=&output=embed',
    images: [
      '/images/locations/wakasa-takahama-familiar/1.jpg',
      '/images/locations/wakasa-takahama-familiar/2.jpg',
      '/images/locations/wakasa-takahama-familiar/3.jpg',
      '/images/locations/wakasa-takahama-familiar/4.jpg',
      '/images/locations/wakasa-takahama-familiar/5.jpg'
    ],
    contact: {
      phone: '0770-72-0338',
      csName: 'Takahashi',
      csPhone: '090-8460-0209',
      homepageUrl: 'https://visittakahama.com/'
    }
  },
  {
    slug: 'wakasa-takahama-wada-camp',
    lat: 35.4912,
    lng: 135.5748,
    mapEmbedSrc:
      'https://www.google.com/maps?q=35.4912,135.5748&t=&z=17&ie=UTF8&iwloc=&output=embed',
    images: [
      '/images/locations/wakasa-takahama-wada-camp/1.jpg',
      '/images/locations/wakasa-takahama-wada-camp/2.jpg',
      '/images/locations/wakasa-takahama-wada-camp/3.jpg',
      '/images/locations/wakasa-takahama-wada-camp/4.jpg',
      '/images/locations/wakasa-takahama-wada-camp/5.jpg'
    ],
    contact: {
      phone: '0770-72-0338',
      csName: 'Takahashi',
      csPhone: '090-8460-0209',
      homepageUrl: 'https://visittakahama.com/'
    }
  },
  {
    slug: 'wakasa-takahama-hirata-kazaemon',
    lat: 35.4915,
    lng: 135.5775,
    mapEmbedSrc:
      'https://www.google.com/maps?q=35.4915,135.5775&t=&z=17&ie=UTF8&iwloc=&output=embed',
    images: [
      '/images/locations/wakasa-takahama-hirata-kazaemon/1.jpg',
      '/images/locations/wakasa-takahama-hirata-kazaemon/2.jpg',
      '/images/locations/wakasa-takahama-hirata-kazaemon/3.jpg',
      '/images/locations/wakasa-takahama-hirata-kazaemon/4.jpg',
      '/images/locations/wakasa-takahama-hirata-kazaemon/5.jpg'
    ],
    contact: {
      phone: '0770-72-0338',
      csName: 'Takahashi',
      csPhone: '090-8460-0209',
      homepageUrl: 'https://visittakahama.com/'
    }
  },
  {
    slug: 'wakasa-takahama-muramiya',
    lat: 35.4928,
    lng: 135.5768,
    mapEmbedSrc:
      'https://www.google.com/maps?q=35.4928,135.5768&t=&z=17&ie=UTF8&iwloc=&output=embed',
    images: [
      '/images/locations/wakasa-takahama-muramiya/1.jpg',
      '/images/locations/wakasa-takahama-muramiya/2.jpg',
      '/images/locations/wakasa-takahama-muramiya/3.jpg',
      '/images/locations/wakasa-takahama-muramiya/4.jpg',
      '/images/locations/wakasa-takahama-muramiya/5.jpg'
    ],
    contact: {
      phone: '0770-72-0338',
      csName: 'Takahashi',
      csPhone: '090-8460-0209',
      homepageUrl: 'https://visittakahama.com/'
    }
  },
  {
    slug: 'rentamo-happy-stay-rin',
    // happy stay 凛 rin — vacation rental, Ichimonji, Sakaide.
    lat: 34.3232,
    lng: 133.8655,
    mapEmbedSrc:
      'https://www.google.com/maps?q=34.3232,133.8655&t=&z=17&ie=UTF8&iwloc=&output=embed',
    images: [
      '/images/locations/rentamo-happy-stay-rin/1.jpg',
      '/images/locations/rentamo-happy-stay-rin/2.jpg',
      '/images/locations/rentamo-happy-stay-rin/3.jpg',
      '/images/locations/rentamo-happy-stay-rin/4.jpg',
      '/images/locations/rentamo-happy-stay-rin/5.jpg'
    ],
    contact: {
      phone: '050-3131-2347',
      csName: 'Takahashi',
      csPhone: '090-8460-0209',
      homepageUrl: 'https://rideblink.weebly.com/rentamo-rent-a-cycle_en.html'
    }
  },
  {
    slug: 'rentamo-b-place-sakade',
    // B-Place Sakade — Airbnb apartments near JR Sakaide Station.
    lat: 34.3150,
    lng: 133.8615,
    mapEmbedSrc:
      'https://www.google.com/maps?q=34.3150,133.8615&t=&z=17&ie=UTF8&iwloc=&output=embed',
    images: [
      '/images/locations/rentamo-b-place-sakade/1.jpg',
      '/images/locations/rentamo-b-place-sakade/2.jpg',
      '/images/locations/rentamo-b-place-sakade/3.jpg',
      '/images/locations/rentamo-b-place-sakade/4.jpg',
      '/images/locations/rentamo-b-place-sakade/5.jpg'
    ],
    contact: {
      phone: '070-2162-9250',
      csName: 'Takahashi',
      csPhone: '090-8460-0209',
      homepageUrl: 'https://rideblink.weebly.com/rentamo-rent-a-cycle_en.html'
    }
  },
  {
    slug: 'rentamo-hostel-oe',
    // Hostel OE — guesthouse in Kamojima, Yoshinogawa, Tokushima.
    lat: 34.0710,
    lng: 134.3555,
    mapEmbedSrc:
      'https://www.google.com/maps?q=34.0710,134.3555&t=&z=17&ie=UTF8&iwloc=&output=embed',
    images: [
      '/images/locations/rentamo-hostel-oe/1.jpg',
      '/images/locations/rentamo-hostel-oe/2.jpg',
      '/images/locations/rentamo-hostel-oe/3.jpg',
      '/images/locations/rentamo-hostel-oe/4.jpg',
      '/images/locations/rentamo-hostel-oe/5.jpg'
    ],
    contact: {
      phone: '0883-26-0117',
      csName: 'Takahashi',
      csPhone: '090-8460-0209',
      homepageUrl: 'https://hostel-oe.com/'
    }
  },
  {
    slug: 'rentamo-cycle-land-yamane',
    // Cycle Land Yamane — bicycle shop in Imazato, Takamatsu.
    lat: 34.3277,
    lng: 134.0618,
    mapEmbedSrc:
      'https://www.google.com/maps?q=34.3277,134.0618&t=&z=17&ie=UTF8&iwloc=&output=embed',
    images: [
      '/images/locations/rentamo-cycle-land-yamane/1.jpg',
      '/images/locations/rentamo-cycle-land-yamane/2.jpg',
      '/images/locations/rentamo-cycle-land-yamane/3.jpg',
      '/images/locations/rentamo-cycle-land-yamane/4.jpg',
      '/images/locations/rentamo-cycle-land-yamane/5.jpg'
    ],
    contact: {
      phone: '087-866-8325',
      csName: 'Takahashi',
      csPhone: '090-8460-0209',
      homepageUrl: 'https://cyclandyamane-smilering.com/'
    }
  }
]

export const getLocationBySlug = (slug: string): LocationMeta | undefined =>
  locations.find((l) => l.slug === slug)

export const getLocationSlugs = (): string[] => locations.map((l) => l.slug)
